import { useId, useState } from "react";

import Link from "@whoppah/next/link";
import { useTranslation } from "next-i18next";
import NextImage from "next/image";

import { cn } from "@/common/utils/css.utils";
import {
  PLP_SEO_KEYS,
  plpSeoFallback,
} from "@/components/search/plp-seo/plp-seo.i18n";
import { PLP_SEO_LEAF_HEADING_SIZE } from "@/components/search/plp-seo/plp-seo.tokens";
import type { PlpEditorialLink } from "@/data/plp-editorial-links";
import { useContentLocale } from "@/i18n/hooks";
import { Heading } from "@/ui/cva/Heading";

export const FEATURED_GUIDE_COUNT = 3;

/** Thumbnail edge in px; review round 2 asked for about 120px instead of a 300px cover. */
const THUMBNAIL_PX = 120;

type GuideCardsProps = {
  /** Curated guides for this PLP, in order; the first three become cards. */
  guides: PlpEditorialLink[];
  /** Every other guide, shown as a list behind "View all guides". */
  more: { href: string; title: string }[];
};

/**
 * One guide as a compact row: a square thumbnail on the left, title and reading time directly
 * next to it. No card chrome, no stretched cover, no space reserved between image and title
 * (review round 2, items 2 and 3). On mobile the thumbnail is 80px and the title clamps to
 * three lines, so a long title does not stack six lines tall next to the image.
 */
const GuideCard = ({ guide }: { guide: PlpEditorialLink }) => {
  const { t } = useTranslation();
  const locale = useContentLocale();

  return (
    <li className="w-[85%] max-w-80 shrink-0 snap-start md:w-auto md:max-w-none">
      <Link
        href={guide.href}
        className="group flex items-start gap-3 rounded-[10px] py-1"
      >
        {guide.image ? (
          <NextImage
            src={guide.image.url}
            width={THUMBNAIL_PX}
            height={THUMBNAIL_PX}
            alt={guide.image.alt}
            sizes={`${THUMBNAIL_PX}px`}
            className="size-20 shrink-0 rounded-[10px] object-cover md:size-[7.5rem]"
          />
        ) : (
          <div
            aria-hidden
            className="size-20 shrink-0 rounded-[10px] bg-flavour-100 md:size-[7.5rem]"
          />
        )}
        <div className="flex min-w-0 flex-col gap-1">
          <Heading
            as="h4"
            size={PLP_SEO_LEAF_HEADING_SIZE}
            className="line-clamp-3 text-grey-800 group-hover:underline"
          >
            {guide.title}
          </Heading>
          {guide.readingMinutes ? (
            <span className="font-body text-sm text-grey-600">
              {t(PLP_SEO_KEYS.minRead, plpSeoFallback.minRead(locale), {
                minutes: guide.readingMinutes,
              })}
            </span>
          ) : null}
        </div>
      </Link>
    </li>
  );
};

/**
 * The editorial guides for a PLP: the top three as thumbnail rows with title and reading time,
 * the rest as a plain list behind a "View all guides" toggle (WHO-5534). On mobile the rows
 * scroll horizontally. Every link is in the DOM either way, so the internal link equity the
 * block exists for is unchanged; only the wall of 37 bullets is gone.
 */
export const GuideCards = ({ guides, more }: GuideCardsProps) => {
  const { t } = useTranslation();
  const locale = useContentLocale();
  const [expanded, setExpanded] = useState(false);
  const listId = useId();

  const featured = guides.slice(0, FEATURED_GUIDE_COUNT);
  const rest = [...guides.slice(FEATURED_GUIDE_COUNT), ...more];

  if (featured.length === 0 && rest.length === 0) {
    return null;
  }

  return (
    <div>
      {featured.length > 0 && (
        <ul className="-mx-4 flex snap-x gap-4 overflow-x-auto px-4 md:mx-0 md:grid md:grid-cols-3 md:gap-6 md:overflow-visible md:px-0">
          {featured.map(guide => (
            <GuideCard key={guide.href} guide={guide} />
          ))}
        </ul>
      )}
      {rest.length > 0 && (
        <>
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={listId}
            onClick={() => setExpanded(value => !value)}
            className="mt-1 inline-flex min-h-11 items-center font-body text-base text-brand-500 underline"
          >
            {expanded
              ? t(PLP_SEO_KEYS.showLess, "Show less")
              : t(
                  PLP_SEO_KEYS.viewAllGuides,
                  plpSeoFallback.viewAllGuides(locale)
                )}
          </button>
          <ul
            id={listId}
            className={cn(
              "mt-1 columns-1 gap-8 space-y-1 font-body text-base leading-6 md:columns-2",
              !expanded && "hidden"
            )}
          >
            {rest.map(guide => (
              <li key={guide.href} className="break-inside-avoid">
                <Link
                  href={guide.href}
                  className="break-words text-brand-500 underline"
                >
                  {guide.title}
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};
